/* eslint-disable complexity */
import { useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { TypeAhead } from '@care/react-component-lib';
import { Icon24Chevron } from '@care/react-embrace-icons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  Grid,
  NativeSelect,
  useMediaQuery,
  Typography,
  Theme,
} from '@mui/material';
import { ChangeEvent, SyntheticEvent, useState } from 'react';
import { find, isArray, isEmpty } from 'lodash-es';
import { COST_GUIDE_LOCATION } from '@/API/costGuideLocation';
import { COST_GUIDE_SERVICE_SPECIALTIES } from '@/API/costGuideServiceSpecialties';
import {
  costGuideLocation as costGuideLocationQueryType,
  costGuideLocationVariables,
} from '@/__generated__/costGuideLocation';
import { costGuide_costGuide_CostGuideSuccess_costGuide_searchDefaults as SearchDefaults } from '@/__generated__/costGuide';
import { costGuideServiceSpecialties_costGuideServiceSpecialties_CostGuideServiceSpecialtiesSuccess_serviceSpecialties_specialties as costGuideServiceSpecialtiesType } from '@/__generated__/costGuideServiceSpecialties';
import { TypeAheadOptions } from '@/types/common';
import { createTypeAheadOptions } from '@/utils/createTypeAheadOptions';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import { ServiceIdForMember, UI_FEATURE_FLAGS, mapStringtoUsStateCode } from '@/constants';

const {
  publicRuntimeConfig: { NEXT_PUBLIC_APP_URL },
} = getConfig();

interface VerticalOption {
  label: string;
  value: string;
}

interface VerticalOptions {
  [key: string]: VerticalOption;
}

interface CostGuideSlimSearchProps {
  searchDefaults?: SearchDefaults;
  segmentation?: string;
  isLandingPage?: boolean;
  expanded?: boolean;
  costGuideServiceSpecialties?: costGuideServiceSpecialtiesType[];
}

export const costGuideVerticalOptions: VerticalOptions = {
  CHILD_CARE: { label: 'Child care', value: 'child-care' },
  BABYSITTER: { label: 'Babysitters', value: 'babysitters' },
  NANNY: { label: 'Nannies', value: 'nannies' },
  SENIOR_CARE: { label: 'Senior care', value: 'senior-care' },
  PET_CARE: { label: 'Pet care', value: 'pet-care' },
  HOUSEKEEPING: { label: 'Housekeeping', value: 'housekeeping' },
  TUTORING: { label: 'Tutoring', value: 'tutors' },
  CARE_GIGS: { label: 'Personal assistants', value: 'personal-assistants' },
};

const CostGuideSlimSearch = ({
  searchDefaults,
  isLandingPage = false,
  segmentation = undefined,
  expanded = false,
  costGuideServiceSpecialties = [],
}: CostGuideSlimSearchProps) => {
  const router = useRouter();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const defaultSearchLocation = searchDefaults?.location
    ? `${searchDefaults.location.location.city}, ${searchDefaults.location.location.state.stateCode}`
    : '';

  const defaultSearchLocationSlug = searchDefaults?.location
    ? `${searchDefaults.location.slug}`
    : '';

  const getPreSelectedVertical = (): string => {
    if (
      searchDefaults?.service?.serviceId &&
      costGuideVerticalOptions[searchDefaults.service.serviceId]
    ) {
      return costGuideVerticalOptions[searchDefaults.service.serviceId].value;
    }

    if (segmentation) {
      return (
        find(costGuideVerticalOptions, (vertical) => vertical.value === segmentation)?.value || ''
      );
    }

    return '';
  };

  const preSelectedVertical: string = getPreSelectedVertical();
  const [selectedVertical, setSelectedVertical] = useState<string>(preSelectedVertical);
  const [locationInputValue, setLocationInputValue] = useState(defaultSearchLocation);
  const [locationValue, setLocationValue] = useState(defaultSearchLocationSlug);
  const [noMatchMessage, setNoMatchMessage] = useState('Enter city name');

  const [locationOptions, setLocationOptions] = useState<TypeAheadOptions[] | []>([]);
  const [getLocationOptions] = useLazyQuery<costGuideLocationQueryType, costGuideLocationVariables>(
    COST_GUIDE_LOCATION,
    {
      onCompleted: (data: costGuideLocationQueryType) => {
        const refinedOptions =
          data?.costGuideLocation.__typename === 'CostGuideLocationSuccess'
            ? data.costGuideLocation.cityList
            : [];

        if (refinedOptions.length === 0) setNoMatchMessage('No options');
        setLocationOptions(createTypeAheadOptions(refinedOptions));
      },
    }
  );

  const getPreSelectedSpecialty = (): string => {
    if (searchDefaults?.service?.serviceId && costGuideServiceSpecialties) {
      return (
        costGuideServiceSpecialties.find(
          (specialty) => specialty.slug === searchDefaults.service.slug
        )?.slug || ''
      );
    }

    return '';
  };

  const preSelectedSpecialty: string = getPreSelectedSpecialty();
  const specialtiesDropdownFlag = useFeatureFlag(UI_FEATURE_FLAGS.COST_GUIDE_SPECIALTY.id);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>(preSelectedSpecialty);
  const [specialtyOptions, setSpecialtyOptions] = useState<costGuideServiceSpecialtiesType[]>(
    costGuideServiceSpecialties
  );

  const [getSpecialitiesOptions] = useLazyQuery<any, any>(COST_GUIDE_SERVICE_SPECIALTIES, {
    onCompleted: (data: any) => {
      const refinedOptions =
        data?.costGuideServiceSpecialties?.__typename === 'CostGuideServiceSpecialtiesSuccess'
          ? data.costGuideServiceSpecialties.serviceSpecialties.specialties
          : [];

      setSpecialtyOptions(refinedOptions);
    },
  });

  const showSpecialtiesDropdown: boolean =
    typeof specialtiesDropdownFlag !== undefined && specialtiesDropdownFlag?.variationIndex === 1;

  const getServiceIdBySegmentation = (segmentationValue: string) => {
    return Object.keys(costGuideVerticalOptions).find(
      (key: string) => costGuideVerticalOptions[key].value === segmentationValue
    );
  };

  const handleVerticalChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedVertical(event.target.value);
    const selectedServiceId: string | undefined = getServiceIdBySegmentation(event.target.value);
    if (selectedServiceId) {
      setSelectedSpecialty('');
      getSpecialitiesOptions({
        variables: {
          serviceId: ServiceIdForMember[selectedServiceId as keyof typeof ServiceIdForMember],
        },
      });
    }
  };

  const handleSpecialtyChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedSpecialty(event.target.value);
  };

  const handleSearchButton = () => {
    const url = !isEmpty(selectedSpecialty)
      ? `/cost/${selectedSpecialty}/${locationValue}`
      : `/cost/${selectedVertical}/${locationValue}`;

    const amplitudeData = {
      cta_text: 'Search',
      cta_type: 'button',
      url: `${NEXT_PUBLIC_APP_URL}${url}`,
      url_path: url,
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
    router.push(url, undefined, { shallow: false });
  };

  const [isSearchAccordionExpanded, setIsSearchAccordionExpanded] = useState<boolean>(expanded);
  const handleSearchAccordionChange = () => {
    setIsSearchAccordionExpanded((prevState: boolean) => !prevState);
  };

  const formatInputValue = (inputValue: string): string => {
    const inputValues = inputValue.split(' ');
    const cityInput = inputValues.slice(0, -1).join(' ');
    const stateCodeInput = inputValues[inputValues.length - 1];
    const stateCode = mapStringtoUsStateCode[stateCodeInput.toUpperCase()];

    if (
      typeof stateCode !== 'undefined' &&
      (inputValues.length === 2 || inputValues.length === 3) &&
      !inputValue.includes(',')
    ) {
      return [cityInput, stateCodeInput].join(', ');
    }
    if (inputValues.length === 2 && stateCodeInput.length === 3) {
      return inputValue.replace(',', '');
    }

    return inputValue;
  };

  const VerticalSelect = (
    <FormControl
      variant="standard"
      sx={{
        width: '100%',
        backgroundColor: 'background.default',
        borderRadius: '12px',
      }}>
      <NativeSelect
        IconComponent={Icon24Chevron}
        onChange={handleVerticalChange}
        value={selectedVertical}
        inputProps={{
          'aria-label': 'CostGuideVertical',
          name: 'CostGuideVertical',
        }}>
        <option key="landing-page" value="" disabled>
          Choose category
        </option>
        {Object.entries(costGuideVerticalOptions).map((vertical) => (
          <option key={vertical[0]} value={vertical[1].value}>
            {vertical[1].label}
          </option>
        ))}
      </NativeSelect>
    </FormControl>
  );

  const SpecialtiesSelect = (
    <FormControl
      variant="standard"
      sx={{
        width: '100%',
        backgroundColor: 'background.default',
        borderRadius: '12px',
      }}>
      <NativeSelect
        IconComponent={Icon24Chevron}
        onChange={handleSpecialtyChange}
        value={selectedSpecialty}
        inputProps={{
          'aria-label': 'CostGuideSpecialties',
          name: 'CostGuideSpecialties',
        }}>
        <option key="specialized-skills" value="">
          Specialized service
        </option>
        {specialtyOptions &&
          Object.entries(specialtyOptions).map((specialty: any) => (
            <option key={specialty[0]} value={specialty[1].slug}>
              {specialty[1].name}
            </option>
          ))}
      </NativeSelect>
    </FormControl>
  );

  const TypeAheadComponent = (
    <TypeAhead
      placeholder="Choose location"
      noMatchMessage={noMatchMessage}
      id="location-autocomplete"
      enablePortal
      multiple={false}
      options={locationOptions}
      inputValue={locationInputValue}
      clearOnBlur={false}
      selectOnFocus={false}
      openOnFocus={false}
      onInputChange={(_event: SyntheticEvent, newValue: string, reason: string) => {
        // prevent input from clearing the default location on initial load
        if (newValue === '' && reason === 'reset') return;
        const value = formatInputValue(newValue);
        setLocationInputValue(value);
        if (newValue !== '') {
          getLocationOptions({
            variables: { criteria: { location: value }, limit: 10 },
          });
        } else {
          setLocationOptions([]);
          setNoMatchMessage('Enter city name');
        }
      }}
      value={{
        label: locationInputValue,
        value: locationValue,
      }}
      onValueChange={(updated) => {
        if (!isArray(updated)) {
          setLocationValue(updated.value);
        }
      }}
      sx={(theme: Theme) => ({
        backgroundColor: theme.palette.care.white,
        borderRadius: '12px',

        '& .MuiInputBase-root': {
          borderRadius: '12px !important',
        },

        '& .MuiInputBase-input': {
          border: `solid 1px ${theme.palette.care.grey[300]} !important`,
          borderRadius: '12px !important',
          fontWeight: '400 !important',
        },
      })}
    />
  );

  const SearchButton = (
    <Button
      disabled={!(selectedVertical && locationValue)}
      color="primary"
      variant="contained"
      size="medium"
      sx={(theme: Theme) => ({
        border: 'none',
        mt: 1.5,
        mb: 1.5,

        width: '100%',
        height: '40px',
        fontSize: '1.125rem',
        [theme.breakpoints.down('lg')]: {
          maxWidth: '320px',
          fontSize: theme.spacing(2),
        },
      })}
      onClick={handleSearchButton}>
      Search
    </Button>
  );

  const MobileView = (
    <Box
      sx={{
        pt: 1,
      }}>
      <Accordion
        elevation={0}
        expanded={isSearchAccordionExpanded}
        onChange={handleSearchAccordionChange}>
        <AccordionSummary expandIcon={<Icon24Chevron />}>
          <Typography variant="h3" color="text.primary">
            Search Care Costs
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ py: 1, px: 2 }}>
          <Grid container direction="column" spacing={2}>
            <Grid item xs>
              {VerticalSelect}
            </Grid>
            {showSpecialtiesDropdown && (
              <Grid item xs>
                {SpecialtiesSelect}
              </Grid>
            )}
            <Grid item xs>
              {TypeAheadComponent}
            </Grid>
            <Grid item xs>
              <Grid container justifyContent="center">
                {SearchButton}
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const DesktopView = (
    <Box
      sx={(theme: Theme) => ({
        backgroundColor: theme.palette.background.primary,
        padding: theme.spacing(1),
        textAlign: 'center',
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(2, 0),
        },
      })}>
      <Box width={showSpecialtiesDropdown ? 965 : 800} mx="auto" px={1} py={2}>
        <Box width="100%" pb={2} display="inline-block">
          <Typography variant="h3" color="text.primary" textAlign="left">
            Search Care Costs
          </Typography>
        </Box>
        <Box width="100%">
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={10}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs>
                  {VerticalSelect}
                </Grid>
                {showSpecialtiesDropdown && (
                  <Grid item xs>
                    {SpecialtiesSelect}
                  </Grid>
                )}
                <Grid item xs>
                  {TypeAheadComponent}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={2}>
              {SearchButton}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );

  const MobileViewLandingPage = (
    <Grid container direction="column" spacing={2}>
      <Grid item xs>
        {VerticalSelect}
      </Grid>
      {showSpecialtiesDropdown && (
        <Grid item xs>
          {SpecialtiesSelect}
        </Grid>
      )}
      <Grid item xs>
        {TypeAheadComponent}
      </Grid>
      <Grid item xs>
        <Grid container justifyContent="center">
          {SearchButton}
        </Grid>
      </Grid>
    </Grid>
  );

  const DesktopViewLandingPage = (
    <Grid container alignItems="center" spacing={2}>
      <Grid item xs={10}>
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs>
            {VerticalSelect}
          </Grid>
          {showSpecialtiesDropdown && (
            <Grid item xs>
              {SpecialtiesSelect}
            </Grid>
          )}
          <Grid item xs>
            {TypeAheadComponent}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={2}>
        {SearchButton}
      </Grid>
    </Grid>
  );

  if (isLandingPage) {
    return isMobile ? MobileViewLandingPage : DesktopViewLandingPage;
  }

  return isMobile ? MobileView : DesktopView;
};

export default CostGuideSlimSearch;
